import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import CompanyChip from '../../components/asset-company/CompanyChip';
import ContractForm from '../../components/contracts/ContractForm';
import { useConfirm } from '../../components/ConfirmDialog';
import { downloadFile } from '../../lib/assets';
import { relative } from '../../lib/when';
import { PERIOD_LABEL, dayWords, shortDay } from '../../lib/licences';
import {
  contractsApi, errorOf, STATUS_LABEL, DOC_LABEL, statusTone, nextWords, capWords, rateWords, noRateWords
} from '../../lib/contracts';
import '../../components/Assets.css';
import '../../components/licences/Licences.css';
import '../../components/contracts/Contracts.css';

/**
 * ONE CONTRACT (120): who it is with, its dates and the notice deadline, the
 * reminders said, the rate card - add a rate, remove one - the caps and how
 * much of them approved work has used, the paperwork (each document shared
 * with the contractor or not), the notes and its whole history.
 * Administrators only. A draft can be made active or deleted; an active one
 * ended or cancelled; nothing goes back to draft.
 */
function Facts({ rows }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (!shown.length) return <p className="as-quiet">Nothing recorded.</p>;
  return (
    <dl className="as-facts">
      {shown.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
    </dl>
  );
}

const sizeWords = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const KIND = { end: 'the end', renewal: 'renewal', notice: 'the notice deadline' };

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [problem, setProblem] = useState('');
  const [options, setOptions] = useState(null);
  const [editing, setEditing] = useState(false);
  const [rate, setRate] = useState({ hourly_rate: '', effective_from: '', effective_to: '', category_id: '' });
  const [docType, setDocType] = useState('contract');
  const [share, setShare] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  const [confirm, confirmDialog] = useConfirm();

  const load = useCallback(() => contractsApi.get(id)
    .then((d) => { setData(d); setError(''); })
    .catch((e) => setError(e?.response?.status === 404 ? 'That contract does not exist.' : errorOf(e, 'Could not load the contract.'))), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    contractsApi.options().then(setOptions).catch(() => setOptions({ contractors: [], companies: [], people: [], categories: [] }));
  }, []);
  const closeForm = useCallback(() => setEditing(false), []);

  const act = async (fn, fallback) => {
    setProblem('');
    try { await fn(); return true; } catch (e) { setProblem(errorOf(e, fallback)); return false; }
  };

  const back = <p className="lic-back"><Link to="/manage/contracts">← Contracts</Link></p>;
  if (error) return <div className="con-page">{back}<div className="empty-state"><p>{error}</p></div></div>;
  if (!data) return <div className="con-page"><p className="as-quiet" aria-busy="true">Loading the contract…</p></div>;

  const k = data.contract;
  const nx = nextWords(k);
  const hours = capWords(k.cap.hours, 'hours', k.currency);
  const money = capWords(k.cap.amount, 'amount', k.currency);
  // The work inside its dates the rate card has no rate for (3 Nov).
  const noRate = data.no_rate || { count: 0, tickets: [] };
  const who = k.contractor_company || k.contractor_name;

  const setStatus = async (status) => {
    const words = {
      active: { title: `Make ${k.title} active?`, confirmLabel: 'Make active',
        message: 'Its rates are used for work done inside its dates, and reminders go out.' },
      ended: { title: `End ${k.title}?`, confirmLabel: 'End the contract', danger: true,
        message: 'No more reminders. Work done while it ran is still priced at its rates.' },
      cancelled: { title: `Cancel ${k.title}?`, confirmLabel: 'Cancel the contract', danger: true,
        message: 'Its rates are no longer used, and no more reminders go out.' }
    }[status];
    const reason = await confirm({
      ...words,
      prompt: status === 'active' ? null : { label: 'Why (optional)', placeholder: 'Replaced by the 2027 contract', required: false }
    });
    if (reason === false) return;
    await act(async () => { setData(await contractsApi.status(k.id, status, typeof reason === 'string' ? reason : '')); }, 'The status could not be changed.');
  };

  const remove = async () => {
    if (!(await confirm({ title: `Delete ${k.title}?`, message: 'It is a draft, so nothing used it. The history keeps a line saying it was deleted.', confirmLabel: 'Delete', danger: true }))) return;
    await act(async () => { await contractsApi.remove(k.id); navigate('/manage/contracts'); }, 'The contract could not be deleted.');
  };

  const addRate = async (e) => {
    e.preventDefault();
    if (rate.hourly_rate === '') { setProblem('Say what the hourly rate is.'); return; }
    const ok = await act(async () => {
      setData(await contractsApi.addRate(k.id, {
        hourly_rate: rate.hourly_rate, effective_from: rate.effective_from || null, effective_to: rate.effective_to || null,
        category_id: rate.category_id || null
      }));
    }, 'The rate could not be added.');
    if (ok) setRate({ hourly_rate: '', effective_from: '', effective_to: '', category_id: '' });
  };

  const removeRate = async (r) => {
    if (!(await confirm({ title: `Remove the ${r.category_name ? `${r.category_name} ` : 'default '}rate of ${rateWords(r.hourly_rate, k.currency)}?`,
      message: 'Invoices already written keep the rate they were written at.', confirmLabel: 'Remove', danger: true }))) return;
    await act(async () => { setData(await contractsApi.removeRate(k.id, r.id)); }, 'The rate could not be removed.');
  };

  const upload = async (e) => {
    e.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) { setProblem('Choose a file first.'); return; }
    if (file.size > 10 * 1024 * 1024) { setProblem('That file is larger than 10 MB.'); return; }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    form.append('doc_type', docType);
    form.append('shareable', share ? 'true' : 'false');
    await act(async () => { await contractsApi.upload(k.id, form); if (fileInput.current) fileInput.current.value = ''; await load(); }, 'The file could not be attached.');
    setUploading(false);
  };

  const toggleShare = (d) => act(async () => { await contractsApi.share(k.id, d.id, !d.shareable); await load(); }, 'That could not be changed.');
  const removeDoc = async (d) => {
    if (!(await confirm({ title: `Remove ${d.file_name}?`, confirmLabel: 'Remove', danger: true }))) return;
    await act(async () => { await contractsApi.removeDocument(k.id, d.id); await load(); }, 'The file could not be removed.');
  };

  return (
    <div className="con-page" data-testid="contract-page" data-contract={k.id}>
      {back}
      <div className="lic-head">
        <div className="lic-head-text">
          <h1 className="page-title">{k.title}</h1>
          <p className="page-description" style={{ margin: 0 }}>
            <Link to={`/contractor-profile/${k.contractor_id}`}>{k.contractor_name}</Link>
            {k.reference ? ` · ${k.reference}` : ''}
          </p>
          <div className="lic-head-chips">
            <span className="as-chip" data-tone={statusTone(k.status)} data-testid="contract-status">{STATUS_LABEL[k.status]}</span>
            {nx && <span className="as-chip" data-tone={nx.tone}>{nx.text}</span>}
            {k.auto_renew && <span className="as-chip">Renews by itself</span>}
          </div>
        </div>
        <div className="lic-head-actions">
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>Change</button>
          {k.status === 'draft' && <button type="button" className="btn btn-primary" onClick={() => setStatus('active')}>Make active</button>}
          {['ended', 'cancelled'].includes(k.status) && <button type="button" className="btn btn-plain" onClick={() => setStatus('active')}>Reactivate</button>}
          {k.status === 'active' && <button type="button" className="btn btn-plain" onClick={() => setStatus('ended')}>End the contract</button>}
          {['draft', 'active'].includes(k.status) && <button type="button" className="btn btn-plain" onClick={() => setStatus('cancelled')}>Cancel the contract</button>}
          {k.status === 'draft' && <button type="button" className="btn btn-danger" onClick={remove}>Delete</button>}
        </div>
      </div>

      {problem && <p className="as-error" role="alert">{problem}</p>}
      {k.overdue && (
        <p className="as-note" data-tone="warning" role="status">
          {k.next_kind === 'end' ? 'Its end date has passed.' : 'Its renewal date has passed and nobody has moved it on. If it was renewed, change the date; if not, end it.'}
        </p>
      )}

      {noRate.count > 0 && (
        <p className="as-note" data-tone="warning" role="status" data-testid="no-rate-banner">
          {`${noRate.count} ticket${noRate.count === 1 ? '' : 's'} in this contract’s dates ${noRate.count === 1 ? 'has' : 'have'} no contract rate for the day worked, so ${noRate.count === 1 ? 'it is' : 'they are'} priced at ${who}’s own rate. Add a rate that covers ${noRate.count === 1 ? 'that day' : 'those days'} to the rate card below.`}
        </p>
      )}

      <div className="as-grid">
        <section className="as-panel" data-card="facts">
          <h2>The contract</h2>
          <Facts rows={[
            ['With', k.contractor_name],
            ['For', k.company_name ? <CompanyChip name={k.company_name} color={k.company_color} code={k.company_code} /> : 'No particular company'],
            ['Reference', k.reference],
            ['Scope', k.scope],
            ['Looked after by', k.owner_name ? `${k.owner_name}${k.owner_active === false ? ' (deactivated)' : ''}` : 'Nobody in particular']
          ]} />
        </section>
        <section className="as-panel" data-card="dates">
          <h2>Dates</h2>
          <Facts rows={[
            ['Starts', k.start_date ? dayWords(k.start_date) : null],
            ['Ends', k.end_date ? dayWords(k.end_date) : 'Runs until ended'],
            ['Renews', k.renewal_date ? `${dayWords(k.renewal_date)}${k.billing_period ? ` (${PERIOD_LABEL[k.billing_period].toLowerCase()})` : ''}` : null],
            ['Notice period', k.notice_days ? `${k.notice_days} day${k.notice_days === 1 ? '' : 's'}` : null],
            ['Notice by', k.notice_deadline ? <strong data-testid="notice-deadline">{dayWords(k.notice_deadline)}</strong> : null],
            ['Reminders said', data.notices.length
              ? data.notices.map((n) => `${n.stage} days before ${KIND[n.kind]} (${shortDay(n.due_on)})`).join('; ')
              : (k.status === 'active' ? 'None yet - 90, 30 and 7 days before' : null)]
          ]} />
        </section>
        <section className="as-panel" data-card="caps">
          <h2>Caps</h2>
          {!hours && !money ? <p className="as-quiet">No cap. Approved work so far: {`${k.used_hours} hours`}.</p> : (
            <div className="con-caps" data-testid="contract-caps">
              {[['Hours', 'hours', hours], ['Money', 'amount', money]].filter(([, , c]) => c).map(([label, kind, c]) => (
                <div key={kind} className="con-cap" data-cap={kind}>
                  <span className="lic-stat-label">{label}</span>
                  <span className="as-chip" data-tone={c.tone}>{c.text}</span>
                  <span className="lic-bar" data-tone={c.tone === 'success' ? undefined : c.tone} aria-hidden="true">
                    <span style={{ width: `${Math.min(100, c.percent)}%` }} />
                  </span>
                </div>
              ))}
              <p className="lic-hint">{`From ${k.used_tickets} approved job${k.used_tickets === 1 ? '' : 's'} inside its dates. Reminders at 80% and 100%.`}</p>
            </div>
          )}
        </section>
      </div>

      <section className="as-panel lic-wide" data-card="rates" aria-label="Rate card">
        <div className="lic-panel-head">
          <h2>Rate card</h2>
          <span className="as-chip" data-tone={data.rate_now?.default ? 'success' : 'idle'} data-testid="rate-now">
            {data.rate_now?.default ? `Today: ${rateWords(data.rate_now.default.hourly_rate, k.currency)}` : `Today: ${k.contractor_name}’s own rate`}
          </span>
        </div>
        {data.rates.length === 0 ? <p className="as-quiet">No rates yet - work is priced at the contractor’s own rate.</p> : (
          <ul className="lic-seats" aria-label="Rates">
            {data.rates.map((r) => (
              <li key={r.id} data-rate={r.id}>
                <span className="lic-seat-who">
                  <span className="lic-seat-kind">{r.category_name || 'Default'}</span>
                  <strong>{rateWords(r.hourly_rate, k.currency)}</strong>
                  <span className="lic-seat-sub">{`From ${shortDay(r.effective_from)}${r.effective_to ? ` to ${shortDay(r.effective_to)}` : ''}${r.note ? ` · ${r.note}` : ''}`}</span>
                </span>
                <button type="button" className="btn btn-plain btn-small" onClick={() => removeRate(r)}
                  aria-label={`Remove the ${r.category_name || 'default'} rate of ${rateWords(r.hourly_rate, k.currency)}`}>Remove</button>
              </li>
            ))}
          </ul>
        )}
        {k.status !== 'cancelled' && (
          <form className="lic-give con-rate-form" onSubmit={addRate} aria-label="Add a rate">
            <label>
              Hourly rate
              <input type="number" inputMode="decimal" min="0" step="0.01" value={rate.hourly_rate}
                onChange={(e) => setRate((x) => ({ ...x, hourly_rate: e.target.value }))} aria-label="Hourly rate" />
            </label>
            <label>
              From
              <input type="date" value={rate.effective_from} onChange={(e) => setRate((x) => ({ ...x, effective_from: e.target.value }))} aria-label="The rate starts" />
            </label>
            <label>
              To (optional)
              <input type="date" value={rate.effective_to} onChange={(e) => setRate((x) => ({ ...x, effective_to: e.target.value }))} aria-label="The rate stops" />
            </label>
            <label>
              For
              <select value={rate.category_id} onChange={(e) => setRate((x) => ({ ...x, category_id: e.target.value }))} aria-label="The rate is for">
                <option value="">Any work (the default)</option>
                {(options?.categories || []).map((c) => <option key={c.id} value={c.id}>{`${c.name} tickets`}</option>)}
              </select>
            </label>
            <button type="submit" className="btn btn-secondary">Add the rate</button>
          </form>
        )}
        {noRate.count > 0 && (
          <div className="con-norate" data-testid="no-rate-list">
            <h3>Work with no contract rate</h3>
            <ul className="lic-seats" aria-label="Work with no contract rate">
              {noRate.tickets.map((t) => (
                <li key={t.id} data-ticket={t.id}>
                  <span className="lic-seat-who">
                    <Link to={`/tickets/${t.id}`} className="lic-seat-kind">{t.ticket_number}</Link>
                    <strong>{t.title}</strong>
                    <span className="lic-seat-sub">
                      {noRateWords({ day: t.day, rate: t.rate, who })}{t.invoiced ? ' · already invoiced' : ''}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
            {noRate.count > noRate.tickets.length && (
              <p className="lic-hint">{`And ${noRate.count - noRate.tickets.length} more.`}</p>
            )}
          </div>
        )}
        <p className="lic-hint">A new rate ends the open one before it the day before. Invoices already written are never changed.</p>
      </section>

      <div className="as-grid">
        <section className="as-panel" data-card="documents" aria-label="Documents">
          <h2>Documents</h2>
          {data.documents.length === 0 ? <p className="as-quiet">Nothing attached.</p> : (
            <ul className="lic-docs">
              {data.documents.map((d) => (
                <li key={d.id} data-doc={d.id}>
                  <span className="lic-doc-name">
                    <button type="button" className="btn btn-plain btn-small" style={{ paddingInline: 0 }}
                      onClick={() => act(() => downloadFile(contractsApi.documentPath(k.id, d.id), d.file_name), 'The file could not be downloaded.')}>
                      {d.file_name}
                    </button>
                    <span className="lic-seat-sub">
                      {`${DOC_LABEL[d.doc_type] || d.doc_type} · ${sizeWords(d.byte_size)} · ${shortDay(d.created_at)}${d.shareable ? ' · shared with the contractor' : ''}`}
                    </span>
                  </span>
                  <button type="button" className="btn btn-plain btn-small" onClick={() => toggleShare(d)}>
                    {d.shareable ? 'Stop sharing' : 'Share with the contractor'}
                  </button>
                  <button type="button" className="btn btn-plain btn-small" onClick={() => removeDoc(d)} aria-label={`Remove ${d.file_name}`}>Remove</button>
                </li>
              ))}
            </ul>
          )}
          <form className="lic-upload" onSubmit={upload}>
            <label>
              Kind
              <select value={docType} onChange={(e) => setDocType(e.target.value)} aria-label="Kind of document">
                {Object.entries(DOC_LABEL).map(([v, w]) => <option key={v} value={v}>{w}</option>)}
              </select>
            </label>
            <label>
              File (10 MB at most)
              <input type="file" ref={fileInput} aria-label="File to attach" />
            </label>
            <label className="lic-check">
              <input type="checkbox" checked={share} onChange={(e) => setShare(e.target.checked)} />
              <span>The contractor may see it</span>
            </label>
            <button type="submit" className="btn btn-secondary" disabled={uploading}>{uploading ? 'Attaching…' : 'Attach'}</button>
          </form>
        </section>
        <section className="as-panel" data-card="notes">
          <h2>Notes</h2>
          {k.notes ? <p className="lic-notes">{k.notes}</p> : <p className="as-quiet">No notes.</p>}
          <p className="lic-hint">The contractor never sees these.</p>
        </section>
      </div>

      <section className="as-panel lic-wide" data-card="history" aria-label="History">
        <h2>History</h2>
        {data.history.length === 0 ? <p className="as-quiet">Nothing yet.</p> : (
          <ul className="as-history">
            {data.history.map((h) => (
              <li key={h.id}>
                <div>{h.said}</div>
                <div className="as-quiet" style={{ fontSize: '0.8rem' }}>
                  {`${h.actor_name}${h.on_behalf_by ? ` (by ${h.on_behalf_by})` : ''} · ${relative(h.at)}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {editing && (
        <ContractForm contract={k} options={options} onClose={closeForm}
          onSaved={(saved) => { setEditing(false); setData(saved); }} />
      )}
      {confirmDialog}
    </div>
  );
}
